/**
 * Cheap structural sniff for a **bundle** archive (`.glz`, spec §4).
 *
 * A bundle is a zip with `scene.gltf` (or a legacy `scene.glxf`) at its root.
 * The extension is a hint, never the authority: a bundle reaches a reader as
 * `.glz`, as a legacy `.tracex`, and — because users rename files — as `.zip`
 * or as nothing recognisable at all. So the dispatcher asks the BYTES.
 *
 * Why hand-rolled rather than JSZip: the sniff runs on every archive open,
 * including the legacy Unity `.zip` scenes that are NOT bundles, and it must
 * not cost a parse of the whole archive to answer "no". Reading the End Of
 * Central Directory record and walking the central directory touches a few
 * hundred bytes and decompresses nothing.
 *
 * Pure module by contract: no Babylon, no I/O, no plugin imports.
 */

/** `PK\x03\x04` — the local file header every non-empty zip starts with. */
const LOCAL_HEADER_SIGNATURE = 0x04034b50;
/** `PK\x05\x06` — End Of Central Directory. */
const EOCD_SIGNATURE = 0x06054b50;
/** `PK\x01\x02` — one central directory file header. */
const CENTRAL_HEADER_SIGNATURE = 0x02014b50;
/** EOCD is 22 bytes plus a comment of at most 0xFFFF. */
const MAX_EOCD_SEARCH = 22 + 0xffff;
/** Fixed size of a central directory file header, before the variable fields. */
const CENTRAL_HEADER_SIZE = 46;

/**
 * View whatever the loader pipeline is holding as bytes.
 *
 * `instanceof ArrayBuffer` is false for a buffer that crossed a realm — a
 * worker, a vm context, the test environment — and JSZip's type check is
 * exactly that `instanceof`, so an archive handed straight from
 * `Blob.arrayBuffer()` can be rejected as "not a supported type" with nothing
 * wrong with the bytes. Normalizing to a `Uint8Array` here is what makes the
 * archive path realm-proof; the same landmine is documented on `asBytes` in
 * `formats/gltf21/normalize.ts`.
 */
export function toZipBytes(data: unknown): Uint8Array {
    if (data instanceof Uint8Array) return data;
    if (ArrayBuffer.isView(data)) {
        return new Uint8Array(data.buffer as ArrayBuffer, data.byteOffset, data.byteLength);
    }
    // Covers a same-realm ArrayBuffer and a foreign one alike: the Uint8Array
    // constructor reads the buffer's length, it does not `instanceof` it.
    return new Uint8Array(data as ArrayBuffer);
}

/** Whether these bytes open with a zip local file header. */
export function isZipData(bytes: Uint8Array): boolean {
    if (bytes.length < 4) return false;
    return new DataView(bytes.buffer, bytes.byteOffset, 4).getUint32(0, true) === LOCAL_HEADER_SIGNATURE;
}

/**
 * The entry names in a zip's central directory, or `undefined` when the bytes
 * are not a zip this reader can enumerate (truncated, zip64, corrupt).
 *
 * `undefined` means "don't know", never "empty" — every caller must treat it
 * as "not a bundle" rather than as "a bundle with no entries".
 */
export function zipEntryNames(bytes: Uint8Array): string[] | undefined {
    if (!isZipData(bytes)) return undefined;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

    // EOCD sits at the very end unless the archive carries a comment. Scan
    // backwards over the largest a comment may be.
    const searchFrom = Math.max(0, bytes.length - MAX_EOCD_SEARCH);
    let eocd = -1;
    for (let i = bytes.length - 22; i >= searchFrom; i--) {
        if (view.getUint32(i, true) === EOCD_SIGNATURE) {
            eocd = i;
            break;
        }
    }
    if (eocd < 0) return undefined;

    const entryCount = view.getUint16(eocd + 10, true);
    let offset = view.getUint32(eocd + 16, true);
    // Zip64 parks 0xFFFF / 0xFFFFFFFF here and puts the real values in its own
    // record. Nothing this writer produces is zip64; say "don't know".
    if (entryCount === 0xffff || offset === 0xffffffff) return undefined;

    const names: string[] = [];
    const decoder = new TextDecoder();
    for (let i = 0; i < entryCount; i++) {
        if (offset + CENTRAL_HEADER_SIZE > bytes.length) return undefined;
        if (view.getUint32(offset, true) !== CENTRAL_HEADER_SIGNATURE) return undefined;
        const nameLength = view.getUint16(offset + 28, true);
        const extraLength = view.getUint16(offset + 30, true);
        const commentLength = view.getUint16(offset + 32, true);
        const nameStart = offset + CENTRAL_HEADER_SIZE;
        if (nameStart + nameLength > bytes.length) return undefined;
        names.push(decoder.decode(bytes.subarray(nameStart, nameStart + nameLength)));
        offset = nameStart + nameLength + extraLength + commentLength;
    }
    return names;
}

/**
 * Whether these bytes are a bundle: a zip carrying one of `entries` at its
 * root. Pass {@link SCENE_DOCUMENT_ARCHIVE_ENTRIES}.
 */
export function isBundleArchive(bytes: Uint8Array, entries: readonly string[]): boolean {
    const names = zipEntryNames(bytes);
    if (!names) return false;
    return names.some(name => entries.includes(name));
}
